import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
// import i18n from 'i18next';

/* Resolve any language value — a name ("english") or an already-BCP-47 tag
 * ("en", "pt-br", "en-US") — to a BCP-47 tag. getLanguageCode maps names to
 * tags; when it misses, the value is already a tag, so fall back to it. Used
 * for both <html lang> and the exposed `locale` so they never diverge. */
const toLocaleTag = (value) =>
  value ? getLanguageCode(value) || value : undefined;

/* RTL scripts by BCP-47 primary subtag. Keyed by tag (not name) so detection
 * works whether the resolved language is a name ("arabic") or a code ("ar"). */
const RTL_LANGUAGE_TAGS = ['ar', 'he'];

function useLanguage(){
    const location = useLocation();
    const urlParams = useMemo(()=> {return new URLSearchParams(location.search)},[location.search]);
    const lang = urlParams.get('lang');
    const [userLanguage, setUserLanguage] = useState(null);

  
    const { t, i18n } = useTranslation();
  
    useEffect(() => {
      // initialize i18next with catalog and language to use

      // 1st priority -> "lang" param
      // 2nd priority -> userLanguage from API
      // 3rd priority -> browser language
      const resolvedLang =
      lang || userLanguage || getLanguageName(navigator.language);

      i18n.changeLanguage(resolvedLang);

      /* Normalize once (with a safe fallback) so dir and lang agree whether
       * resolvedLang is a name ("arabic") or already a tag ("ar", "en-US"). */
      const localeTag = toLocaleTag(resolvedLang) || 'en';
      const primarySubtag = localeTag.split('-')[0].toLowerCase();
      document.dir = RTL_LANGUAGE_TAGS.includes(primarySubtag) ? 'rtl' : 'ltr';
      document.documentElement.lang = localeTag;

      const pElements = document.querySelectorAll("p");
      if (primarySubtag !== 'en') {
        pElements.forEach((pElement)=>{
          pElement.classList.add("not-lang");
        });
      } else {
        pElements.forEach((pElement)=>{
          pElement.classList.remove("not-lang");
        });
      }
      
    }, [i18n, urlParams ,lang, userLanguage]);

    /* Expose the same BCP-47 tag we set on <html lang> so consumers can format
     * dates/numbers deterministically for the selected UI language instead of
     * relying on the browser default. */
    const locale = toLocaleTag(i18n.language);

    return {t, setUserLanguage, locale};
}

function getLanguageName(code) {
    const languageCode = code.split('-')[0];
    switch (languageCode) {
      case 'ar':
        return 'arabic';
      
      case 'bg':
        return 'bulgarian';

      case 'hr':
        return 'croatian';

      case 'cs':
        return 'czech';

      case 'da':
        return 'danish';

      case 'nl':
        return 'dutch';

      case 'en':
        return 'english';

      case 'fi':
        return 'finnish';

      case 'fr':
        return 'french';

      case 'de':
        return 'german';

      case 'el':
        return 'greek';

      case 'hu':
        return 'hungarian';

      case 'id':
        return 'indonesian';

      case 'it':
        return 'italian';

      case 'ja':
        return 'japanese';

      case 'ko':
        return 'korean';

      case 'ms':
        return 'malay';

      case 'no':
        return 'norwegian';

      case 'pl':
        return 'polish';

      case 'pt':
        return 'portuguese';

      case 'ro':
        return 'romanian';

      case 'sr':
        return 'serbian';

      case 'sk':
        return 'slovak';

      case 'sl':
        return 'slovenian';

      case 'es':
        return 'spanish';

      case 'sv':
        return 'swedish';

      case 'th':
        return 'thai';

      case 'tr':
        return 'turkish';

      case 'uk':
        return 'ukrainian';

      case 'he':
        return 'hebrew';

      case 'zh-hk':
        return 'cantonese';

      case 'zh-cn':
        return 'chinese';

      case 'hi':
        return 'hindi';

      case 'pt-br':
        return 'portuguese_br';
      
      case 'tk':
        return 'turkmen';

      case 'vi':
        return 'vietnamese';

      case 'bs':
        return 'bosnian';

      case 'zu':
        return 'zulu';

      case 'ru':
        return 'russian';

      case 'et':
        return 'estonian';

      case 'lv':
        return 'latvian';
        
      case 'lt':
        return 'lithuanian';

      case 'fil':
        return 'filipino';
      
      case 'bn':
        return 'bengali';

      default:
        console.error('Language code not define');
        return 'english';
      
    }
}

function getLanguageCode(name) {

  const languageMap = {
    arabic: 'ar',
    bulgarian: 'bg',
    croatian: 'hr',
    czech: 'cs',
    danish: 'da',
    dutch: 'nl',
    english: 'en',
    finnish: 'fi',
    french: 'fr',
    german: 'de',
    greek: 'el',
    hungarian: 'hu',
    indonesian: 'id',
    italian: 'it',
    japanese: 'ja',
    korean: 'ko',
    malay: 'ms',
    norwegian: 'no',
    polish: 'pl',
    portuguese: 'pt',
    romanian: 'ro',
    serbian: 'sr',
    slovak: 'sk',
    slovenian: 'sl',
    spanish: 'es',
    swedish: 'sv',
    thai: 'th',
    turkish: 'tr',
    ukrainian: 'uk',
    hebrew: 'he',
    cantonese: 'zh-hk',
    chinese: 'zh-cn',
    hindi: 'hi',
    portuguese_br: 'pt-br',
    turkmen: 'tk',
    vietnamese: 'vi',
    bosnian: 'bs',
    zulu: 'zu',
    russian: 'ru',
    estonian: 'et',
    latvian: 'lv',
    lithuanian: 'lt',
    filipino: 'fil',
    bengali: 'bn',
  };

  return languageMap[name.toLowerCase()];
}


export default useLanguage;